import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import axios from "axios";
import { API_BASE } from "@/lib/track";
import { LogoMark } from "@/components/Logo";

export default function ProductViewPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session") || "";
  const [state, setState] = useState("loading");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/orders/${encodeURIComponent(sessionId)}`);
        if (data.status === "ready" && data.content) {
          setOrder(data);
          setState("ready");
          return;
        }
        setState("processing");
        timer = setTimeout(poll, 4000);
      } catch (e) {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-obsidian px-5 py-14 sm:py-20 grain">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="product-view-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors"
        >
          All In Hand Marketing
        </Link>

        {state === "loading" && (
          <div className="mt-24 text-center text-slate-400 flex items-center justify-center gap-3" data-testid="product-view-loading">
            <Loader2 className="w-5 h-5 animate-spin text-gold" /> Loading your product…
          </div>
        )}

        {state === "processing" && (
          <div className="mt-16 rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-10 text-center" data-testid="product-view-processing">
            <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-6">Preparing your product…</h1>
            <p className="text-slate-400 text-sm mt-3">
              This usually takes a few seconds. The page opens automatically when it's ready — you can
              also check your email.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="mt-16 rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-10 text-center" data-testid="product-view-error">
            <h1 className="font-display text-2xl font-bold text-white">Order not found</h1>
            <p className="text-slate-400 text-sm mt-3">
              Use the link from your purchase email, or contact us and we'll help.
            </p>
            <Link
              data-testid="product-view-error-home"
              to="/"
              className="inline-block mt-8 rounded-full border border-gold/60 text-gold font-display font-bold px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {state === "ready" && order && (
          <div className="mt-8" data-testid="product-view-document">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <LogoMark className="w-11 h-11" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">My All In Hand · Digital Product</p>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{order.product_name}</h1>
                </div>
              </div>
              <button
                data-testid="product-view-print"
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-full border border-gold/60 text-gold font-display font-bold px-5 py-2.5 text-sm hover:bg-gold hover:text-obsidian transition-colors"
              >
                <Printer className="w-4 h-4" /> Print / Save PDF
              </button>
            </div>

            <div className="mt-8 rounded-3xl border border-gold/25 bg-obsidian-800/60 p-7 sm:p-12 space-y-8">
              {order.content.map((s, i) => (
                <div key={i}>
                  <h2 className="font-display font-bold text-gold text-lg">{s.title}</h2>
                  <p className="text-slate-200 text-sm leading-relaxed mt-3 whitespace-pre-line">{s.body}</p>
                </div>
              ))}
              <p className="text-slate-600 text-xs leading-relaxed border-t border-slate-800 pt-6">
                Delivered by All In Hand Marketing · 7-day money-back guarantee on eligible purchases.
                Need help applying this? Book a consultation and we'll do it with you.
              </p>
            </div>

            <Link
              to="/intake?type=consultation"
              data-testid="product-view-consultation"
              className="inline-block mt-8 rounded-full bg-gold text-obsidian font-display font-bold px-8 py-4 hover:bg-gold-bright transition-colors"
            >
              Book a Consultation
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
